import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { Button } from '@/components/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InheritanceSourceBadge, type InheritanceSourceKind } from '@/components/InheritanceSourceBadge';
import { TimeRangeInput, type TimeRangeInputValue } from '@/components/TimeRangeInput';
import { ConfirmDialog, Modal } from '@/components/Modal';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/settings/attendance` (v0.4 章⑤ F-4、D-13/D-37/D-93)。
 * 勤怠設定 (割増率・法定労働時間・休憩・変形労働時間制・締日/支払日等) と、法定値マスタとの対応表示。
 * ⚠️ D-93: 法定最低基準を既定値として表示し、最終確認日を必ず添える。テナントが上書きした場合、
 * その確認日は「プラットフォーム提供値についての確認日」であり設定値自体の確認日ではないことを明示する。
 * 設計: docs/design/v04-shift-labor-management-detail.md §5-6 / §5-10
 */

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];
const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
// ⚠️ design-expert Critical指摘: type="number" はネイティブスピナーを抑制すること
// (NotificationsSettingsPage.tsx/Agreement36SettingsPage.tsx/VisitorCountsPage.tsx で確立済みのパターン)
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;
const DOW_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

function SingleTimeSelect({ label, value, onChange, disabled }: { label: string; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const [h, m] = value.split(':');
  return (
    <div className="flex items-center gap-1">
      <Select value={h} onValueChange={(nh) => onChange(`${nh}:${m ?? '00'}`)} disabled={disabled}>
        <SelectTrigger className="w-[68px] min-h-[44px]" aria-label={`${label}(時)`}><SelectValue /></SelectTrigger>
        <SelectContent>{HOURS.map((hh) => <SelectItem key={hh} value={hh}>{hh}</SelectItem>)}</SelectContent>
      </Select>
      <span className="text-text-sub" aria-hidden>:</span>
      <Select value={m} onValueChange={(nm) => onChange(`${h ?? '00'}:${nm}`)} disabled={disabled}>
        <SelectTrigger className="w-[68px] min-h-[44px]" aria-label={`${label}(分)`}><SelectValue /></SelectTrigger>
        <SelectContent>{MINUTES.map((mm) => <SelectItem key={mm} value={mm}>{mm}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function NodeSelector({ nodeId, onChange }: { nodeId: string; onChange: (id: string) => void }) {
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  return (
    <div className="min-w-0 w-full sm:w-72">
      <label className="block text-xs text-text-sub mb-1">対象ノード</label>
      <Select value={nodeId || undefined} onValueChange={onChange}>
        <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="ノードを選ぶ" /></SelectTrigger>
        <SelectContent>
          {flatNodes.map(({ node, depth }) => (
            <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

type EmploymentType = { type_id: string; code: string; label: string };
const ALL_TYPES_VALUE = '__all__';

function EmploymentTypeSelector({ typeId, onChange }: { typeId: string | null; onChange: (id: string | null) => void }) {
  const { data: types = [] } = useQuery<EmploymentType[]>({ queryKey: ['employment-types'], queryFn: () => apiFetch<EmploymentType[]>('/employment-types') });
  return (
    <div className="min-w-0 w-full sm:w-64">
      <label className="block text-xs text-text-sub mb-1">雇用形態</label>
      <Select value={typeId ?? ALL_TYPES_VALUE} onValueChange={(v) => onChange(v === ALL_TYPES_VALUE ? null : v)}>
        <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_TYPES_VALUE}>全雇用形態 (既定)</SelectItem>
          {types.map((t) => <SelectItem key={t.type_id} value={t.type_id}>{t.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

type LegalConstant = { constant_key: string; value: Record<string, unknown>; unit: string | null; label: string; explanation: string; source_citation: string; last_verified_on: string };

const LEGAL_KEY_BY_FIELD: Record<string, string> = {
  overtimeRate: 'overtime_rate_standard',
  overtimeRateOver60h: 'overtime_rate_over_60h_monthly',
  nightRate: 'night_work_rate',
  holidayRate: 'statutory_holiday_rate',
  dailyStatutoryMinutes: 'daily_statutory_working_minutes',
  weeklyStatutoryMinutes: 'weekly_statutory_working_minutes',
  // v0.4 追加7要件⑨ (D-256/Q-197、2026-08-24 user承認): 軸A (付与要件) のみ。軸B (特定方法) は
  // 行政解釈を含むため意図的に法定値マスタへ投入しない (control migration 0008 のコメント参照)。
  statutoryHolidayGrantUnit: 'statutory_holiday_grant',
};

const formatLegalValue = (c: LegalConstant): string => {
  if (typeof c.value.rate === 'number') return `${c.value.rate}倍`;
  if (typeof c.value.minutes === 'number') return `${c.value.minutes}分 (${(c.value.minutes / 60).toFixed(1)}時間)`;
  if (typeof c.value.start === 'string' && typeof c.value.end === 'string') return `${c.value.start}〜${c.value.end}`;
  if (typeof c.value.weekly_days === 'number' && typeof c.value.four_week_days === 'number') {
    return `週${c.value.weekly_days}日、または4週${c.value.four_week_days}日 (変形休日制)`;
  }
  return JSON.stringify(c.value);
};

/** D-93: 法定最低基準の参照情報。テナントが上書きしていても「プラットフォーム提供値についての
 *  確認日」であることが伝わるよう、常にこの1行を添える (上書き値自体の確認日ではない旨を明記)。 */
function LegalReference({ constant, overridden }: { constant: LegalConstant | undefined; overridden: boolean }) {
  if (!constant) return null;
  return (
    <p className="text-xs text-text-sub mt-1">
      法定最低基準: <span className="font-semibold text-text-body">{formatLegalValue(constant)}</span>
      {' '}(出典: {constant.source_citation}、最終確認日: {constant.last_verified_on})
      {overridden && (
        <span className="mt-1 flex items-start gap-1 text-alert-text">
          <AlertTriangle size={12} className="shrink-0 mt-0.5" aria-hidden />
          この確認日はプラットフォーム提供の法定最低基準についてのものです。現在の設定値はテナントが独自に設定したもので、その値自体の確認日ではありません。
        </span>
      )}
    </p>
  );
}

type BreakRule = { overMinutes: number; breakMinutes: number };
type StatutoryHolidayGrantUnit = 'weekly' | 'four_week';
type StatutoryHolidayDesignation = 'fixed_dow' | 'last_rest_day' | 'first_rest_day' | 'none';
// v0.4後半 月次実績一括入力 第2フェーズ I-1a (§8-223/G-4): 従来は保存経路の列挙漏れにより
// 保存のたびに既定値へ戻っていた5項目。この画面から編集できるようにする。
type WorkRecordOverlapPolicy = 'block' | 'warn' | 'allow';
type WorkRecordApprovalMode = 'none' | 'required';
type WorkRecordSelfEntryMode = 'manager_only' | 'manager_and_self';
type MonthlyEntryDailyReportMode = 'locked' | 'editable_default';
// v0.4後半 月次実績一括入力 第2フェーズ I-1b (§3-5): 勤怠設定の新項目5点。
type VariableWorkingHoursType = 'none' | 'monthly';
type MonthlyEntryBlankDayMode = 'required' | 'treat_as_off';
type MonthlyEntrySummaryProfile = 'standard' | 'hourly_night_split' | 'days_planned_actual';
type BreakRecordMode = 'minutes' | 'time_range';
// 契約区分マスタの残業計算連携 (§13-42)
type ContractClassStandardHoursMode = 'disabled' | 'avg_month_days' | 'actual_month_days' | 'master_work_days';
type AttendanceValue = {
  businessDayStartTime: string; dailyStatutoryMinutes: number; weeklyStatutoryMinutes: number;
  overtimeRate: number; overtimeRateOver60h: number; nightStartTime: string; nightEndTime: string; nightRate: number;
  holidayRate: number; legalHolidayDow: number | null; minRestIntervalMinutes: number | null;
  // v0.4 追加7要件⑨ (D-255/D-256): 法定休日の特定方式の2軸 (§12)。
  weekStartDow: number; statutoryHolidayGrantUnit: StatutoryHolidayGrantUnit; statutoryHolidayGrantDays: number;
  fourWeekCycleStartDate: string | null; statutoryHolidayDesignation: StatutoryHolidayDesignation;
  breakMode: 'auto_deduct' | 'clock'; breakRules: BreakRule[]; variableWorkingHoursType: VariableWorkingHoursType;
  closingDay: number; paymentMonthOffset: number; paymentDay: number; source: string;
  workRecordOverlapPolicy: WorkRecordOverlapPolicy; workRecordApprovalMode: WorkRecordApprovalMode;
  workRecordSelfEntryMode: WorkRecordSelfEntryMode; complianceForecastIncludesDraft: boolean;
  monthlyEntryDailyReportMode: MonthlyEntryDailyReportMode;
  monthlyEntryBlankDayMode: MonthlyEntryBlankDayMode; monthlyEntrySummaryProfile: MonthlyEntrySummaryProfile;
  breakRecordMode: BreakRecordMode; plannedTimePrefill: boolean; variablePeriodStartDay: number;
  contractClassStandardHoursMode: ContractClassStandardHoursMode;
};

const ATTENDANCE_DEFAULTS: AttendanceValue = {
  businessDayStartTime: '00:00', dailyStatutoryMinutes: 480, weeklyStatutoryMinutes: 2400,
  overtimeRate: 1.25, overtimeRateOver60h: 1.50, nightStartTime: '22:00', nightEndTime: '05:00', nightRate: 1.25,
  holidayRate: 1.35, legalHolidayDow: null, minRestIntervalMinutes: null,
  weekStartDow: 0, statutoryHolidayGrantUnit: 'weekly', statutoryHolidayGrantDays: 1,
  fourWeekCycleStartDate: null, statutoryHolidayDesignation: 'none',
  breakMode: 'auto_deduct', breakRules: [{ overMinutes: 360, breakMinutes: 45 }, { overMinutes: 480, breakMinutes: 60 }],
  variableWorkingHoursType: 'none', closingDay: 31, paymentMonthOffset: 1, paymentDay: 25, source: 'default',
  workRecordOverlapPolicy: 'block', workRecordApprovalMode: 'none', workRecordSelfEntryMode: 'manager_only',
  complianceForecastIncludesDraft: true, monthlyEntryDailyReportMode: 'editable_default',
  monthlyEntryBlankDayMode: 'required', monthlyEntrySummaryProfile: 'standard',
  breakRecordMode: 'minutes', plannedTimePrefill: true, variablePeriodStartDay: 1,
  contractClassStandardHoursMode: 'disabled',
};

const DESIGNATION_LABELS: Record<StatutoryHolidayDesignation, string> = {
  none: '特定しない', fixed_dow: '曜日で固定する', last_rest_day: '週内で最後に休んだ日', first_rest_day: '週内で最初に休んだ日',
};

type PendingReservation = { effective_from: string };
type ResolvedAttendance = {
  value: AttendanceValue | null; source_node_id: string | null; source_kind: InheritanceSourceKind;
  source_node_label: string | null; pending_reservation: PendingReservation | null;
};

const WORK_RECORD_OVERLAP_POLICY_LABELS: Record<WorkRecordOverlapPolicy, string> = {
  block: '禁止する', warn: '警告のみ', allow: '許可する',
};
const WORK_RECORD_APPROVAL_MODE_LABELS: Record<WorkRecordApprovalMode, string> = {
  none: '不要', required: '必要',
};
const WORK_RECORD_SELF_ENTRY_MODE_LABELS: Record<WorkRecordSelfEntryMode, string> = {
  manager_only: '管理者のみ', manager_and_self: '本人も入力可',
};
const MONTHLY_ENTRY_DAILY_REPORT_MODE_LABELS: Record<MonthlyEntryDailyReportMode, string> = {
  locked: '日報の値を優先しロックする', editable_default: '月次画面でも編集可能にする',
};
// v0.4後半 月次実績一括入力 第2フェーズ I-1b (§3-5)
const VARIABLE_WORKING_HOURS_TYPE_LABELS: Record<VariableWorkingHoursType, string> = {
  none: '定めなし', monthly: '1か月単位の変形労働時間制',
};
const MONTHLY_ENTRY_BLANK_DAY_MODE_LABELS: Record<MonthlyEntryBlankDayMode, string> = {
  required: '入力必須にする', treat_as_off: '休みとして扱う',
};
const MONTHLY_ENTRY_SUMMARY_PROFILE_LABELS: Record<MonthlyEntrySummaryProfile, string> = {
  standard: '標準', hourly_night_split: '時間帯別 (深夜分離)', days_planned_actual: '予定・実績の日数対比',
};
// 契約区分マスタの残業計算連携 (§13-42)
const CONTRACT_CLASS_STANDARD_HOURS_MODE_LABELS: Record<ContractClassStandardHoursMode, string> = {
  disabled: '反映しない', avg_month_days: '平均月日数で換算 (365日÷12)',
  actual_month_days: '対象月の実日数で換算', master_work_days: 'マスタの月所定労働日数で換算',
};
const BREAK_RECORD_MODE_LABELS: Record<BreakRecordMode, string> = {
  minutes: '分数で記録', time_range: '時刻範囲で記録',
};

/** 今日 (端末のローカル日付、'YYYY-MM-DD') を返す。サーバー側はノードのタイムゾーンで判定するため
 *  あくまで入力の初期値・簡易ガイド用 (厳密な境界判定はサーバーが行う)。 */
const todayDateInputValue = (): string => new Date().toISOString().slice(0, 10);

function AttendanceForm({
  initial, canSave, onSave, saving, monthlyV2Enabled, pendingReservation, onCancelReservation, onRescheduleReservation, reservationActionPending,
}: {
  initial: AttendanceValue; canSave: boolean; onSave: (v: AttendanceValue, effectiveFrom: string | null) => void; saving: boolean;
  monthlyV2Enabled: boolean; pendingReservation: PendingReservation | null;
  onCancelReservation: () => void; onRescheduleReservation: (newDate: string) => void; reservationActionPending: boolean;
}) {
  // ⚠️ initial は呼び出し側で key (nodeId/employmentTypeId/forceOwn) を切り替えて remount させる
  // ことで再初期化する (useEffect + オブジェクト参照比較にすると、フォーム編集中の親の無関係な
  // 再レンダーで巻き戻る危険がある、ShiftPatternsSettingsPage の initialized フラグ方式より単純)。
  const [form, setForm] = useState<AttendanceValue>(initial);
  // v0.4後半 月次実績一括入力 第2フェーズ I-1a (H-6/J-3): 反映開始日。空欄=今すぐ反映 (従来通り)。
  const [effectiveFromInput, setEffectiveFromInput] = useState('');
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const { data: legalConstants } = useQuery<LegalConstant[]>({ queryKey: ['legal-constants'], queryFn: () => apiFetch<LegalConstant[]>('/legal-constants'), staleTime: 60 * 60 * 1000 });
  const legalByKey = useMemo(() => new Map((legalConstants ?? []).map((c) => [c.constant_key, c])), [legalConstants]);
  const overridden = form.source !== 'default';

  const nightRange: TimeRangeInputValue = { startTime: form.nightStartTime, endTime: form.nightEndTime };

  const addBreakRule = () => setForm({ ...form, breakRules: [...form.breakRules, { overMinutes: 480, breakMinutes: 60 }] });
  const removeBreakRule = (i: number) => setForm({ ...form, breakRules: form.breakRules.filter((_, idx) => idx !== i) });
  const updateBreakRule = (i: number, patch: Partial<BreakRule>) =>
    setForm({ ...form, breakRules: form.breakRules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });

  return (
    <div className="space-y-6">
      {/* v0.4後半 月次実績一括入力 第2フェーズ I-1a (§3-1 画面要件): 未処理の予約の表示は設定パネル
          全体の見出し直下 (項目一覧より上) に置く仕様 (design-expertレビューCritical、8巡目で確定)。
          フラグOFFでは非表示にする (機能フラグでガードする新機能)。 */}
      {monthlyV2Enabled && pendingReservation && (
        <div className="flex items-center justify-between flex-wrap gap-2 rounded border border-alert-border bg-alert-bg p-3">
          <p className="text-sm text-alert-text">{pendingReservation.effective_from} から変更予定です。</p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setRescheduleOpen(true)} disabled={reservationActionPending}>日付を変更</Button>
            <Button variant="secondary" size="sm" onClick={() => setCancelConfirmOpen(true)} disabled={reservationActionPending}>取消</Button>
          </div>
        </div>
      )}
      <fieldset disabled={!canSave} className="space-y-4 disabled:opacity-70">
        <div className="space-y-3">
          <h3 className="text-sm font-bold text-text-body">割増賃金・法定労働時間</h3>
          {/* v0.4 追加要件⑪ 章⑥ (D-362): 4つの割増率は「時間を出す列」と異なり意図的に活性化していない
              (割増賃金額そのものは算出しない、D-346/R-5)。設定はできるが表示・計算には反映されないため
              未使用であることを明記する (§12、正典 §2-4(d) が指摘した誤解を防ぐ)。 */}
          <p className="text-xs text-text-sub">
            以下4つの割増率は現在、表示・計算には使用されていません (法定外労働時間・36協定の遵守状況の算出までに留めています)。
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-sub mb-1">時間外労働の割増率</label>
              <input type="number" step="0.01" min={1} value={form.overtimeRate} onChange={(e) => setForm({ ...form, overtimeRate: Number(e.target.value) || 0 })} className={numberInputCls} />
              <LegalReference constant={legalByKey.get(LEGAL_KEY_BY_FIELD.overtimeRate!)} overridden={overridden} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">月60時間超の割増率</label>
              <input type="number" step="0.01" min={1} value={form.overtimeRateOver60h} onChange={(e) => setForm({ ...form, overtimeRateOver60h: Number(e.target.value) || 0 })} className={numberInputCls} />
              <LegalReference constant={legalByKey.get(LEGAL_KEY_BY_FIELD.overtimeRateOver60h!)} overridden={overridden} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">深夜労働の割増率</label>
              <input type="number" step="0.01" min={1} value={form.nightRate} onChange={(e) => setForm({ ...form, nightRate: Number(e.target.value) || 0 })} className={numberInputCls} />
              <LegalReference constant={legalByKey.get(LEGAL_KEY_BY_FIELD.nightRate!)} overridden={overridden} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">法定休日労働の割増率</label>
              <input type="number" step="0.01" min={1} value={form.holidayRate} onChange={(e) => setForm({ ...form, holidayRate: Number(e.target.value) || 0 })} className={numberInputCls} />
              <LegalReference constant={legalByKey.get(LEGAL_KEY_BY_FIELD.holidayRate!)} overridden={overridden} />
            </div>
          </div>
          <div>
            <TimeRangeInput value={nightRange} onChange={(v) => setForm({ ...form, nightStartTime: v.startTime ?? form.nightStartTime, nightEndTime: v.endTime ?? form.nightEndTime })}
              legend="深夜労働の時間帯" disabled={!canSave} />
            <LegalReference constant={legalByKey.get('night_work_hours')} overridden={overridden} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-sub mb-1">1日の法定労働時間 (分)</label>
              <input type="number" min={1} value={form.dailyStatutoryMinutes} onChange={(e) => setForm({ ...form, dailyStatutoryMinutes: Number(e.target.value) || 0 })} className={numberInputCls} />
              <LegalReference constant={legalByKey.get(LEGAL_KEY_BY_FIELD.dailyStatutoryMinutes!)} overridden={overridden} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">週の法定労働時間 (分)</label>
              <input type="number" min={1} value={form.weeklyStatutoryMinutes} onChange={(e) => setForm({ ...form, weeklyStatutoryMinutes: Number(e.target.value) || 0 })} className={numberInputCls} />
              <LegalReference constant={legalByKey.get(LEGAL_KEY_BY_FIELD.weeklyStatutoryMinutes!)} overridden={overridden} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">勤務間インターバル (分、任意)</label>
              <input type="number" min={0} value={form.minRestIntervalMinutes ?? ''} placeholder="設定しない"
                onChange={(e) => setForm({ ...form, minRestIntervalMinutes: e.target.value === '' ? null : Number(e.target.value) || 0 })} className={numberInputCls} />
            </div>
          </div>
        </div>

        {/* v0.4 追加7要件⑨ (D-255/D-256): 法定休日の特定方式 (軸A=付与要件/軸B=特定方法)。
            労基法35条は曜日固定を要件としないため「曜日」を主入力にしない (§12)。
            既定表示は2項目のみ (§6-0 原則6)、4週4休制は詳細設定に隠す。 */}
        <div className="space-y-3">
          <h3 className="text-sm font-bold text-text-body">法定休日の特定方式</h3>
          <p className="text-xs text-text-sub">
            労働基準法35条は「毎週少なくとも1回」の休日を求めますが、どの曜日を法定休日とするかは定めていません。
            曜日を固定していない場合は「特定しない」のままにしてください。
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-sub mb-1">法定休日の特定方法</label>
              <Select value={form.statutoryHolidayDesignation}
                onValueChange={(v) => setForm({ ...form, statutoryHolidayDesignation: v as StatutoryHolidayDesignation })} disabled={!canSave}>
                <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(DESIGNATION_LABELS) as StatutoryHolidayDesignation[]).map((k) => (
                    <SelectItem key={k} value={k}>{DESIGNATION_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.statutoryHolidayDesignation === 'fixed_dow' && (
              <div>
                <label className="block text-sm text-text-sub mb-1">法定休日の曜日</label>
                <Select value={form.legalHolidayDow === null ? '__none__' : String(form.legalHolidayDow)}
                  onValueChange={(v) => setForm({ ...form, legalHolidayDow: v === '__none__' ? null : Number(v) })} disabled={!canSave}>
                  <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">未選択</SelectItem>
                    {DOW_LABELS.map((l, d) => <SelectItem key={d} value={String(d)}>{l}曜日</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <details className="text-sm">
            <summary className="cursor-pointer text-text-sub min-h-[44px] flex items-center">詳細設定 (付与単位・週の起算日・4週4休制)</summary>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              <div>
                <label className="block text-sm text-text-sub mb-1">法定休日の付与単位</label>
                <Select value={form.statutoryHolidayGrantUnit}
                  onValueChange={(v) => setForm({
                    ...form, statutoryHolidayGrantUnit: v as StatutoryHolidayGrantUnit,
                    // QAレビューLOW対応: weekly に戻したら起算日を無言で残さない (往復時に古い日付が
                    // 復元されると保存値とUI表示が食い違う)
                    fourWeekCycleStartDate: v === 'four_week' ? form.fourWeekCycleStartDate : null,
                  })} disabled={!canSave}>
                  <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">週1日 (原則、労基法35条1項)</SelectItem>
                    <SelectItem value="four_week">4週4日 (変形休日制、35条2項)</SelectItem>
                  </SelectContent>
                </Select>
                <LegalReference constant={legalByKey.get(LEGAL_KEY_BY_FIELD.statutoryHolidayGrantUnit!)} overridden={overridden} />
                {form.statutoryHolidayGrantUnit === 'four_week' && (
                  <p className="mt-1 flex items-start gap-1 text-xs text-alert-text">
                    <AlertTriangle size={12} className="shrink-0 mt-0.5" aria-hidden />
                    4週4休制の採用には就業規則または労使協定の定めが必要です。定めが無い場合は「週1日」を選択してください。
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">付与日数</label>
                <input type="number" min={1} value={form.statutoryHolidayGrantDays}
                  onChange={(e) => setForm({ ...form, statutoryHolidayGrantDays: Number(e.target.value) || 1 })} className={numberInputCls} />
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">週の起算日</label>
                <Select value={String(form.weekStartDow)} onValueChange={(v) => setForm({ ...form, weekStartDow: Number(v) })} disabled={!canSave}>
                  <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOW_LABELS.map((l, d) => <SelectItem key={d} value={String(d)}>{l}曜日</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-text-sub mt-1">既定値は日曜日です。就業規則に定めがある場合は変更してください。</p>
              </div>
              {form.statutoryHolidayGrantUnit === 'four_week' && (
                <div>
                  <label className="block text-sm text-text-sub mb-1">4週サイクルの起算日</label>
                  <input type="date" value={form.fourWeekCycleStartDate ?? ''}
                    onChange={(e) => setForm({ ...form, fourWeekCycleStartDate: e.target.value || null })} className={inputCls} disabled={!canSave} />
                </div>
              )}
            </div>
          </details>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-bold text-text-body">運用ルール</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-sub mb-1">営業日の切替時刻</label>
              <SingleTimeSelect label="営業日切替" value={form.businessDayStartTime} onChange={(v) => setForm({ ...form, businessDayStartTime: v })} disabled={!canSave} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">休憩の扱い</label>
              <div className="flex rounded-full border border-surface-border overflow-hidden w-fit" role="group" aria-label="休憩の扱い">
                {([['auto_deduct', '所定時間を自動控除'], ['clock', '実打刻']] as const).map(([v, label]) => (
                  <button key={v} type="button" aria-pressed={form.breakMode === v} disabled={!canSave} onClick={() => setForm({ ...form, breakMode: v })}
                    className={`px-3 min-h-[44px] text-xs font-medium disabled:opacity-45 ${form.breakMode === v ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {form.breakMode === 'auto_deduct' && (
            <div className="space-y-2">
              <label className="block text-sm text-text-sub">自動控除ルール (労働時間がこの分数を超えたら控除)</label>
              {form.breakRules.map((r, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-text-sub">実働</span>
                  <input type="number" min={0} value={r.overMinutes} onChange={(e) => updateBreakRule(i, { overMinutes: Number(e.target.value) || 0 })}
                    className="w-24 min-h-[44px] px-2 py-2 rounded border border-surface-border appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" aria-label="超過分数" disabled={!canSave} />
                  <span className="text-xs text-text-sub">分超で</span>
                  <input type="number" min={0} value={r.breakMinutes} onChange={(e) => updateBreakRule(i, { breakMinutes: Number(e.target.value) || 0 })}
                    className="w-24 min-h-[44px] px-2 py-2 rounded border border-surface-border appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" aria-label="休憩分数" disabled={!canSave} />
                  <span className="text-xs text-text-sub">分の休憩</span>
                  {canSave && (
                    <button type="button" aria-label="このルールを削除" onClick={() => removeBreakRule(i)}
                      className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                  )}
                </div>
              ))}
              {canSave && <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addBreakRule}>ルールを追加</Button>}
            </div>
          )}
          <div>
            <label className="block text-sm text-text-sub mb-1">変形労働時間制</label>
            <Select value={form.variableWorkingHoursType}
              onValueChange={(v) => setForm({ ...form, variableWorkingHoursType: v as VariableWorkingHoursType })} disabled={!canSave}>
              <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(VARIABLE_WORKING_HOURS_TYPE_LABELS) as VariableWorkingHoursType[]).map((k) => (
                  <SelectItem key={k} value={k}>{VARIABLE_WORKING_HOURS_TYPE_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* design-expertレビュー Major対応: 4週4休制 (§12) と同じ法定要件を持つのに警告が無かった
                (労基法32条の2、労使協定または就業規則の定めが必須)。 */}
            {form.variableWorkingHoursType === 'monthly' && (
              <p className="mt-1 flex items-start gap-1 text-xs text-alert-text">
                <AlertTriangle size={12} className="shrink-0 mt-0.5" aria-hidden />
                1か月単位の変形労働時間制の採用には就業規則または労使協定の定めが必要です (労基法32条の2)。定めが無い場合は「定めなし」を選択してください。
              </p>
            )}
          </div>
          {form.variableWorkingHoursType === 'monthly' && (
            <div>
              <label className="block text-sm text-text-sub mb-1">変形期間の起算日 (日)</label>
              <input type="number" min={1} max={28} value={form.variablePeriodStartDay}
                onChange={(e) => setForm({ ...form, variablePeriodStartDay: Number(e.target.value) || 1 })} className={numberInputCls} />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-text-sub mb-1">締日 (日)</label>
              <input type="number" min={1} max={31} value={form.closingDay} onChange={(e) => setForm({ ...form, closingDay: Number(e.target.value) || 1 })} className={numberInputCls} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">支払月オフセット</label>
              <Select value={String(form.paymentMonthOffset)} onValueChange={(v) => setForm({ ...form, paymentMonthOffset: Number(v) })} disabled={!canSave}>
                <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">当月払い</SelectItem>
                  <SelectItem value="1">翌月払い</SelectItem>
                  <SelectItem value="2">翌々月払い</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">支払日 (日)</label>
              <input type="number" min={1} max={31} value={form.paymentDay} onChange={(e) => setForm({ ...form, paymentDay: Number(e.target.value) || 1 })} className={numberInputCls} />
            </div>
          </div>
        </div>

        {/* v0.4後半 月次実績一括入力 第2フェーズ I-1a (§8-223/G-4是正): 従来 保存経路の列挙漏れで
            編集不能だった5項目。既存の不具合是正のため機能フラグに関係なく常時編集可能にする。 */}
        <div className="space-y-3">
          <h3 className="text-sm font-bold text-text-body">月次実績一括入力との連携</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-sub mb-1">同日・同時間帯の重複勤務</label>
              <Select value={form.workRecordOverlapPolicy}
                onValueChange={(v) => setForm({ ...form, workRecordOverlapPolicy: v as WorkRecordOverlapPolicy })} disabled={!canSave}>
                <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(WORK_RECORD_OVERLAP_POLICY_LABELS) as WorkRecordOverlapPolicy[]).map((k) => (
                    <SelectItem key={k} value={k}>{WORK_RECORD_OVERLAP_POLICY_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">勤務実績の承認</label>
              <Select value={form.workRecordApprovalMode}
                onValueChange={(v) => setForm({ ...form, workRecordApprovalMode: v as WorkRecordApprovalMode })} disabled={!canSave}>
                <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(WORK_RECORD_APPROVAL_MODE_LABELS) as WorkRecordApprovalMode[]).map((k) => (
                    <SelectItem key={k} value={k}>{WORK_RECORD_APPROVAL_MODE_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">本人による入力</label>
              <Select value={form.workRecordSelfEntryMode}
                onValueChange={(v) => setForm({ ...form, workRecordSelfEntryMode: v as WorkRecordSelfEntryMode })} disabled={!canSave}>
                <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(WORK_RECORD_SELF_ENTRY_MODE_LABELS) as WorkRecordSelfEntryMode[]).map((k) => (
                    <SelectItem key={k} value={k}>{WORK_RECORD_SELF_ENTRY_MODE_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">日報の欄の扱い (月次画面)</label>
              <Select value={form.monthlyEntryDailyReportMode}
                onValueChange={(v) => setForm({ ...form, monthlyEntryDailyReportMode: v as MonthlyEntryDailyReportMode })} disabled={!canSave}>
                <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(MONTHLY_ENTRY_DAILY_REPORT_MODE_LABELS) as MonthlyEntryDailyReportMode[]).map((k) => (
                    <SelectItem key={k} value={k}>{MONTHLY_ENTRY_DAILY_REPORT_MODE_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* v0.4後半 月次実績一括入力 第2フェーズ I-1b (§3-5) */}
            {/* design-expertレビュー Major対応: 「休憩の記録方式」は月次実績一括入力固有の設定のため
                このセクションに属する (以前は「運用ルール」内の休憩の扱いの隣に置いていたが、
                ラベルに「(月次実績一括入力)」という補足を要していたこと自体が場所の誤りを示していた)。 */}
            <div>
              <label className="block text-sm text-text-sub mb-1">休憩の記録方式</label>
              <Select value={form.breakRecordMode}
                onValueChange={(v) => setForm({ ...form, breakRecordMode: v as BreakRecordMode })} disabled={!canSave}>
                <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(BREAK_RECORD_MODE_LABELS) as BreakRecordMode[]).map((k) => (
                    // v0.4後半 月次実績一括入力 第2フェーズ I-2 §4-4 (design-expert指摘対応、2026-09-27):
                    // バックエンドは time_range の休憩入力 (breaks[]) に対応したが、単票入力画面
                    // (WorkRecordModal.tsx) はまだ休憩の時刻範囲を入力するUIを持たない (月次一括入力も
                    // 未対応、§4-4参照)。入力手段が無いまま選べてしまうと保存時に必ず
                    // WORK_RECORD_BREAKS_REQUIRED で行き詰まるため、対応する画面ができるまで選択肢自体を
                    // 無効化しておく (既存の値が既に time_range の場合はそのまま表示・保持する)。
                    <SelectItem key={k} value={k} disabled={k === 'time_range' && form.breakRecordMode !== 'time_range'}>
                      {BREAK_RECORD_MODE_LABELS[k]}{k === 'time_range' ? ' (対応画面 準備中)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.breakRecordMode === 'time_range' && form.breakMode === 'auto_deduct' && (
                <p className="text-xs text-alert-text mt-1">「時刻範囲で記録」を使うには、上の「休憩の扱い」を「実打刻」にしてください。</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">未入力日の扱い</label>
              <Select value={form.monthlyEntryBlankDayMode}
                onValueChange={(v) => setForm({ ...form, monthlyEntryBlankDayMode: v as MonthlyEntryBlankDayMode })} disabled={!canSave}>
                <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(MONTHLY_ENTRY_BLANK_DAY_MODE_LABELS) as MonthlyEntryBlankDayMode[]).map((k) => (
                    <SelectItem key={k} value={k}>{MONTHLY_ENTRY_BLANK_DAY_MODE_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">集計表示のプロファイル</label>
              <Select value={form.monthlyEntrySummaryProfile}
                onValueChange={(v) => setForm({ ...form, monthlyEntrySummaryProfile: v as MonthlyEntrySummaryProfile })} disabled={!canSave}>
                <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(MONTHLY_ENTRY_SUMMARY_PROFILE_LABELS) as MonthlyEntrySummaryProfile[]).map((k) => (
                    <SelectItem key={k} value={k}>{MONTHLY_ENTRY_SUMMARY_PROFILE_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* design-expertレビュー Minor対応: 選択肢のラベルだけでは何が変わるか分かりにくい */}
              <p className="text-xs text-text-sub mt-1">月次画面の集計欄に表示する内訳の種類です (「時間帯別」は深夜分を分離、「予定・実績の日数対比」は出勤日数を予定と比較表示します)。</p>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">契約区分マスタの標準値を残業計算に反映</label>
              <Select value={form.contractClassStandardHoursMode}
                onValueChange={(v) => setForm({ ...form, contractClassStandardHoursMode: v as ContractClassStandardHoursMode })} disabled={!canSave}>
                <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(CONTRACT_CLASS_STANDARD_HOURS_MODE_LABELS) as ContractClassStandardHoursMode[]).map((k) => (
                    <SelectItem key={k} value={k}>{CONTRACT_CLASS_STANDARD_HOURS_MODE_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-text-sub mt-1">
                社員に個人の週契約時間・契約区分 (第1種/第2種/第3種等) の割り当てがある場合、
                「所定(h)」等の解決順序を「個人週契約 → 契約区分マスタ標準値 → 店舗既定 → 未設定」に拡張します。
                「反映しない」の場合は従来通り契約区分マスタを無視します。
              </p>
            </div>
          </div>
          <label className="flex items-center gap-2 min-h-[44px]">
            <input type="checkbox" checked={form.plannedTimePrefill} disabled={!canSave}
              onChange={(e) => setForm({ ...form, plannedTimePrefill: e.target.checked })} />
            <span className="text-sm text-text-body">予定の時刻を入力欄にあらかじめ表示する</span>
          </label>
          <label className="flex items-center gap-2 min-h-[44px]">
            <input type="checkbox" checked={form.complianceForecastIncludesDraft} disabled={!canSave}
              onChange={(e) => setForm({ ...form, complianceForecastIncludesDraft: e.target.checked })} />
            <span className="text-sm text-text-body">36協定見込みの算出に下書き (未提出) の勤務実績も含める</span>
          </label>
        </div>
      </fieldset>

      {canSave && (
        <div className="flex flex-col items-end gap-2">
          {/* design-expertレビューMajor対応: 詳細設定 (<details>) の中でしか分からない理由により
              保存ボタンが無言でdisabledになるのを防ぐ */}
          {form.statutoryHolidayGrantUnit === 'four_week' && !form.fourWeekCycleStartDate && (
            <p className="text-xs text-alert-text">4週サイクルの起算日 (詳細設定内) を入力してください。</p>
          )}
          {monthlyV2Enabled && !pendingReservation && (
            <div className="flex items-center flex-wrap gap-2">
              <label htmlFor="attendance-effective-from" className="text-xs text-text-sub">反映開始日 (空欄なら今すぐ)</label>
              <input id="attendance-effective-from" type="date" min={todayDateInputValue()} value={effectiveFromInput}
                onChange={(e) => setEffectiveFromInput(e.target.value)} className={`${inputCls} w-auto`} />
            </div>
          )}
          <Button variant="primary" loading={saving}
            disabled={form.statutoryHolidayGrantUnit === 'four_week' && !form.fourWeekCycleStartDate}
            onClick={() => onSave(form, effectiveFromInput || null)}>保存</Button>
        </div>
      )}
      <p className="text-xs text-text-sub border-t border-surface-line pt-3">
        この画面で設定した割増率等のパラメータに基づき人件費が計算されます。法令適合の確認はご利用者様の責任で行ってください。
      </p>

      {/* design-expertレビューMajor対応: 予約の取消は非破壊的操作 (元の設定に戻るだけ) なので
          実行ボタンを赤 (破壊的操作専用、docs/ui-guidelines.md §2.2) にしない */}
      <ConfirmDialog
        open={cancelConfirmOpen} title="予約を取り消しますか?" onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => { onCancelReservation(); setCancelConfirmOpen(false); }} confirmLabel="取消する" loading={reservationActionPending}
        confirmVariant="secondary"
        message={pendingReservation && `${pendingReservation.effective_from} からの変更予約を取り消します。`}
      />
      <Modal open={rescheduleOpen} onClose={() => setRescheduleOpen(false)} title="予約の日付を変更">
        <div className="space-y-3">
          <div>
            <label htmlFor="attendance-reschedule-date" className="block text-sm text-text-sub mb-1">新しい反映開始日</label>
            <input id="attendance-reschedule-date" type="date" min={todayDateInputValue()} value={rescheduleDate}
              onChange={(e) => setRescheduleDate(e.target.value)} className={inputCls} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setRescheduleOpen(false)}>キャンセル</Button>
            <Button variant="primary" loading={reservationActionPending} disabled={!rescheduleDate}
              onClick={() => { onRescheduleReservation(rescheduleDate); setRescheduleOpen(false); }}>変更する</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// v0.4 TD-25 (D-261/D-262、§17-4-5): 週所定労働日数・時間の「ノード既定」編集。個人ごとの上書きは
// /employees/:id 側 (§17-4-5 の2箇所目) で行う。読取は generic な
// GET /inheritable-settings/:key/resolve をそのまま使う (この2キーは value_shape='scalar' のため、
// §17-4-2 の訂正どおり汎用ディスパッチにそのまま乗る)。
type ResolvedScalar = { value: number | null; source_kind: InheritanceSourceKind; source_node_label: string | null };

function ContractedHoursSection({ nodeId, canManageAll }: { nodeId: string; canManageAll: boolean }) {
  const qc = useQueryClient();
  const workDaysQuery = useQuery<ResolvedScalar>({
    queryKey: ['inheritable-setting-resolve', 'weekly_contracted_work_days', nodeId],
    queryFn: () => apiFetch<ResolvedScalar>(`/inheritable-settings/weekly_contracted_work_days/resolve?node_id=${nodeId}`),
    enabled: !!nodeId,
  });
  const minutesQuery = useQuery<ResolvedScalar>({
    queryKey: ['inheritable-setting-resolve', 'weekly_contracted_minutes', nodeId],
    queryFn: () => apiFetch<ResolvedScalar>(`/inheritable-settings/weekly_contracted_minutes/resolve?node_id=${nodeId}`),
    enabled: !!nodeId,
  });

  const [workDaysInput, setWorkDaysInput] = useState('');
  const [minutesInput, setMinutesInput] = useState('');
  useEffect(() => setWorkDaysInput(workDaysQuery.data?.value != null ? String(workDaysQuery.data.value) : ''), [workDaysQuery.data]);
  useEffect(() => setMinutesInput(minutesQuery.data?.value != null ? String(minutesQuery.data.value) : ''), [minutesQuery.data]);

  const saveWorkDaysMut = useMutation({
    mutationFn: (value: number | null) => apiFetch('/settings/weekly-contracted-work-days', {
      method: 'PUT', body: JSON.stringify({ node_id: nodeId, value }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['inheritable-setting-resolve', 'weekly_contracted_work_days'] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const saveMinutesMut = useMutation({
    mutationFn: (value: number | null) => apiFetch('/settings/weekly-contracted-minutes', {
      method: 'PUT', body: JSON.stringify({ node_id: nodeId, value }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['inheritable-setting-resolve', 'weekly_contracted_minutes'] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  if (!nodeId) return null;
  if (workDaysQuery.isLoading || minutesQuery.isLoading) return <div className="text-text-sub">読み込み中…</div>;

  return (
    <div className="space-y-2 rounded border border-surface-border p-3">
      <h3 className="text-sm font-semibold text-text-body">所定労働日数・時間 (この組織単位の標準値)</h3>
      <p className="text-xs text-text-sub">個別の雇用契約がある社員は、社員詳細画面から個人ごとに上書きできます。</p>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="weekly-contracted-work-days" className="block text-xs text-text-sub mb-1">週所定労働日数</label>
          <div className="flex items-center gap-2">
            <input
              id="weekly-contracted-work-days" type="number" min={1} className={`${numberInputCls} max-w-[8rem]`}
              value={workDaysInput} disabled={!canManageAll}
              onChange={(e) => setWorkDaysInput(e.target.value)}
            />
            <span className="text-xs text-text-sub">日/週</span>
            <Button size="sm" disabled={!canManageAll || saveWorkDaysMut.isPending} onClick={() => saveWorkDaysMut.mutate(workDaysInput === '' ? null : Number(workDaysInput))}>保存</Button>
          </div>
          <InheritanceSourceBadge sourceKind={workDaysQuery.data?.source_kind ?? 'none'} sourceNodeLabel={workDaysQuery.data?.source_node_label} />
        </div>
        <div>
          <label htmlFor="weekly-contracted-minutes" className="block text-xs text-text-sub mb-1">週所定労働時間 (分)</label>
          <div className="flex items-center gap-2">
            <input
              id="weekly-contracted-minutes" type="number" min={1} className={`${numberInputCls} max-w-[8rem]`}
              value={minutesInput} disabled={!canManageAll}
              onChange={(e) => setMinutesInput(e.target.value)}
            />
            <span className="text-xs text-text-sub">分/週</span>
            <Button size="sm" disabled={!canManageAll || saveMinutesMut.isPending} onClick={() => saveMinutesMut.mutate(minutesInput === '' ? null : Number(minutesInput))}>保存</Button>
          </div>
          <InheritanceSourceBadge sourceKind={minutesQuery.data?.source_kind ?? 'none'} sourceNodeLabel={minutesQuery.data?.source_node_label} />
        </div>
      </div>
    </div>
  );
}

// v0.4後半 月次実績一括入力 第2フェーズ I-1a (§3-1 画面要件): スコープ切替UIに未処理の予約を持つ
// スコープの件数バッジを表示するための一覧。
type ReservationsResponse = { reservations: Array<{ node_id: string | null; employment_type_id: string | null }> };

export function AttendanceSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const permsReady = usePermsReady();
  const canManageAll = hasPermission(user?.effective_permissions, 'tenant_config', 'manage');
  const monthlyV2Enabled = !!user?.tenant_features?.attendance_monthly_v2;
  const [nodeId, setNodeId] = useState('');
  const [employmentTypeId, setEmploymentTypeId] = useState<string | null>(null);
  const [forceOwn, setForceOwn] = useState(false);
  const qc = useQueryClient();

  const query = new URLSearchParams({ node_id: nodeId, ...(employmentTypeId ? { employment_type_id: employmentTypeId } : {}) });
  const { data, isLoading } = useQuery<ResolvedAttendance>({
    queryKey: ['attendance-settings', nodeId, employmentTypeId],
    queryFn: () => apiFetch<ResolvedAttendance>(`/settings/attendance?${query.toString()}`),
    enabled: !!nodeId,
  });

  const { data: reservationsData } = useQuery<ReservationsResponse>({
    queryKey: ['attendance-settings-reservations'],
    queryFn: () => apiFetch<ReservationsResponse>('/settings/attendance/reservations'),
    enabled: monthlyV2Enabled && canManageAll,
  });
  const reservedNodeIds = useMemo(
    () => new Set((reservationsData?.reservations ?? []).map((r) => r.node_id).filter((v): v is string => v !== null)),
    [reservationsData],
  );
  const reservedEmploymentTypeIds = useMemo(
    () => new Set((reservationsData?.reservations ?? []).map((r) => r.employment_type_id).filter((v): v is string => v !== null)),
    [reservationsData],
  );

  useEffect(() => setForceOwn(false), [nodeId, employmentTypeId]);

  const isOwnActual = data?.source_kind === 'self';
  const isOwn = isOwnActual || forceOwn;
  const canSave = canManageAll && isOwn && !!nodeId;

  const saveMut = useMutation({
    mutationFn: ({ v, effectiveFrom }: { v: AttendanceValue; effectiveFrom: string | null }) => apiFetch('/settings/attendance', {
      method: 'PUT',
      body: JSON.stringify({
        node_id: nodeId, employment_type_id: employmentTypeId,
        business_day_start_time: v.businessDayStartTime, daily_statutory_minutes: v.dailyStatutoryMinutes,
        weekly_statutory_minutes: v.weeklyStatutoryMinutes, overtime_rate: v.overtimeRate, overtime_rate_over_60h: v.overtimeRateOver60h,
        night_start_time: v.nightStartTime, night_end_time: v.nightEndTime, night_rate: v.nightRate, holiday_rate: v.holidayRate,
        legal_holiday_dow: v.legalHolidayDow,
        week_start_dow: v.weekStartDow, statutory_holiday_grant_unit: v.statutoryHolidayGrantUnit,
        statutory_holiday_grant_days: v.statutoryHolidayGrantDays,
        four_week_cycle_start_date: v.statutoryHolidayGrantUnit === 'four_week' ? v.fourWeekCycleStartDate : null,
        statutory_holiday_designation: v.statutoryHolidayDesignation,
        min_rest_interval_minutes: v.minRestIntervalMinutes, break_mode: v.breakMode,
        break_rules: v.breakRules.map((r) => ({ over_minutes: r.overMinutes, break_minutes: r.breakMinutes })),
        variable_working_hours_type: v.variableWorkingHoursType, closing_day: v.closingDay,
        payment_month_offset: v.paymentMonthOffset, payment_day: v.paymentDay,
        work_record_overlap_policy: v.workRecordOverlapPolicy, work_record_approval_mode: v.workRecordApprovalMode,
        work_record_self_entry_mode: v.workRecordSelfEntryMode, compliance_forecast_includes_draft: v.complianceForecastIncludesDraft,
        monthly_entry_daily_report_mode: v.monthlyEntryDailyReportMode,
        monthly_entry_blank_day_mode: v.monthlyEntryBlankDayMode, monthly_entry_summary_profile: v.monthlyEntrySummaryProfile,
        break_record_mode: v.breakRecordMode, planned_time_prefill: v.plannedTimePrefill,
        variable_period_start_day: v.variableWorkingHoursType === 'monthly' ? v.variablePeriodStartDay : undefined,
        contract_class_standard_hours_mode: v.contractClassStandardHoursMode,
        ...(effectiveFrom ? { effective_from: effectiveFrom } : {}),
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attendance-settings'] });
      qc.invalidateQueries({ queryKey: ['attendance-settings-reservations'] });
      toast.success('保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const cancelReservationMut = useMutation({
    mutationFn: () => apiFetch('/settings/attendance/reservation/cancel', {
      method: 'POST', body: JSON.stringify({ node_id: nodeId, employment_type_id: employmentTypeId }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attendance-settings'] });
      qc.invalidateQueries({ queryKey: ['attendance-settings-reservations'] });
      toast.success('予約を取り消しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '取消に失敗しました'),
  });

  const rescheduleReservationMut = useMutation({
    mutationFn: (newDate: string) => apiFetch('/settings/attendance/reservation/reschedule', {
      method: 'POST', body: JSON.stringify({ node_id: nodeId, employment_type_id: employmentTypeId, effective_from: newDate }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attendance-settings'] });
      qc.invalidateQueries({ queryKey: ['attendance-settings-reservations'] });
      toast.success('予約日を変更しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '変更に失敗しました'),
  });

  const displayValue: AttendanceValue = data?.value ?? ATTENDANCE_DEFAULTS;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">勤怠・法令設定</h2>
        <p className="text-sm text-text-sub mt-1">割増率・法定労働時間・休憩の扱い等を設定します。未設定の項目は法定最低基準がそのまま適用されます。</p>
        {permsReady && !canManageAll && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です (編集には店舗の管理権限が必要です)。</p>}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <NodeSelector nodeId={nodeId} onChange={setNodeId} />
          {monthlyV2Enabled && reservedNodeIds.size > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-orange text-white text-[10px] font-bold"
              title="予約のある店舗があります">{reservedNodeIds.size}</span>
          )}
        </div>
        <div className="relative">
          <EmploymentTypeSelector typeId={employmentTypeId} onChange={setEmploymentTypeId} />
          {monthlyV2Enabled && reservedEmploymentTypeIds.size > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-orange text-white text-[10px] font-bold"
              title="予約のある雇用形態があります">{reservedEmploymentTypeIds.size}</span>
          )}
        </div>
      </div>

      <ContractedHoursSection nodeId={nodeId} canManageAll={canManageAll} />

      {!nodeId ? (
        <p className="text-sm text-text-sub">対象ノードを選んでください。</p>
      ) : isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (
        <div className="space-y-3">
          {!forceOwn && (
            <InheritanceSourceBadge sourceKind={data?.source_kind ?? 'none'} sourceNodeLabel={data?.source_node_label} />
          )}
          {!isOwn && canManageAll && (
            <div className="flex items-center justify-between flex-wrap gap-2 rounded border border-alert-border bg-alert-bg p-3">
              <p className="text-xs text-alert-text">上位ノード (または法定最低基準) を継承しています。この店舗専用の設定を追加すると、以降はこの店舗独自の定義になります。</p>
              <Button variant="secondary" size="sm" onClick={() => setForceOwn(true)}>この店舗の設定を追加する</Button>
            </div>
          )}
          <AttendanceForm
            key={`${nodeId}:${employmentTypeId ?? ''}:${forceOwn}`} initial={displayValue} canSave={canSave} saving={saveMut.isPending}
            onSave={(v, effectiveFrom) => saveMut.mutate({ v, effectiveFrom })}
            monthlyV2Enabled={monthlyV2Enabled} pendingReservation={data?.pending_reservation ?? null}
            onCancelReservation={() => cancelReservationMut.mutate()}
            onRescheduleReservation={(newDate) => rescheduleReservationMut.mutate(newDate)}
            reservationActionPending={cancelReservationMut.isPending || rescheduleReservationMut.isPending}
          />
        </div>
      )}
    </div>
  );
}
